// src/components/tasks.jsx
// TasksView - todo list backed by HubSpot tasks.
//
// Data flow:
//   - On mount: fetch tasks owned by viewed user(s) from HubSpot.
//   - + New Task: create in HubSpot first, then add to local state.
//   - Toggle done: update HubSpot status, then flip local state.
//   - Delete: delete from HubSpot, then remove local state.
//
// Admin view: dropdown to view "All Users" merged or any single rep.
// Non-admin: shows their own tasks only.

import { useState, useEffect, useMemo } from "react";
import * as HS from "../hubspot.js";
import { Btn, FI, Spin } from "./ui.jsx";
import { findOwner } from "./owners.jsx";

// Same user-color palette as Calendar so the merged view looks consistent
const USER_COLORS = [
  "#b01020", "#1a7a3a", "#9b6ec8", "#e8a838", "#4a9e6e",
  "#c0614a", "#444444", "#6b8cae", "#a8553b", "#5d7e9f",
];
function colorForUser(userId) {
  if (!userId) return "#666666";
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    h = ((h << 5) - h) + userId.charCodeAt(i);
    h |= 0;
  }
  return USER_COLORS[Math.abs(h) % USER_COLORS.length];
}

const TI = {call:"📞", visit:"🤝", email:"✉", quote:"📋", other:"📌"};

export function TasksView({currentUser, users, companies}) {
  const isAdmin = currentUser.isAdmin;
  const [tasks,     setTasks]     = useState([]);
  const [showAdd,   setShowAdd]   = useState(false);
  const [filter,    setFilter]    = useState("all");
  const [nw,        setNw]        = useState({title:"", type:"call", dueDate:"", companyId:"", note:""});
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);  // shown during create/update/delete
  const [errMsg,    setErrMsg]    = useState("");

  // Admin user filter: "all" = everyone, "me" = current admin, "<userId>" = specific rep
  const [viewUser, setViewUser] = useState(isAdmin ? "all" : "me");

  // Resolve which CRM users we're viewing
  const viewedUsers = useMemo(() => {
    if (!isAdmin) return [currentUser];
    if (viewUser === "all") return (users || []).filter(u => !u.isAdmin);
    if (viewUser === "me")  return [currentUser];
    const u = (users || []).find(u => u.id === viewUser);
    return u ? [u] : [];
  }, [isAdmin, viewUser, users, currentUser]);

  // Resolve HubSpot owner IDs for the users we're viewing
  const viewedOwnerIds = useMemo(() => {
    return viewedUsers
      .map(u => u.hubspotOwnerId || (String(u.id || "").startsWith("hs_") ? String(u.id).slice(3) : null))
      .filter(Boolean);
  }, [viewedUsers]);

  // ── Load tasks from HubSpot ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (viewedOwnerIds.length === 0) {
        setTasks([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setErrMsg("");
      try {
        const hsTasks = await HS.getTasksByOwners(viewedOwnerIds);
        if (cancelled) return;
        // Attach companyName for display (we'll associate via HubSpot later
        // -- for now we look up by name if it appears in the task body, or
        // leave blank). Future improvement: pull associations via API.
        setTasks(hsTasks);
      } catch (e) {
        if (!cancelled) setErrMsg("Could not load tasks: " + e.message);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewedOwnerIds.join(",")]);

  // ── Mutations -- update HubSpot first, then local state ───────────────────
  const add = async () => {
    if (!nw.title) return;
    setBusy(true);
    setErrMsg("");
    try {
      // Default new task's owner to whichever user the admin is "acting as"
      // (current rep if non-admin; current admin viewing their own; or admin
      // themselves if they're in "all users" view).
      const targetUser = isAdmin && viewUser !== "me" && viewUser !== "all"
        ? (users || []).find(u => u.id === viewUser)
        : currentUser;
      const ownerId = targetUser?.hubspotOwnerId || null;
      if (!ownerId) {
        setErrMsg("Cannot create task: selected user has no HubSpot Owner ID");
        setBusy(false);
        return;
      }
      const res = await HS.createTask(
        {...nw, ownerId},
        nw.companyId || null,
      );
      if (!res?.id) throw new Error("HubSpot did not return a task ID");
      // Add to local state with the HubSpot-issued ID so future updates work
      const newTask = {
        id:       "hs_" + res.id,
        hsId:     res.id,
        title:    nw.title,
        note:     nw.note,
        type:     nw.type,
        dueDate:  nw.dueDate,
        done:     false,
        ownerId:  String(ownerId),
        companyId: nw.companyId || "",
      };
      setTasks(prev => [newTask, ...prev]);
      setNw({title:"", type:"call", dueDate:"", companyId:"", note:""});
      setShowAdd(false);
    } catch (e) {
      setErrMsg("Could not save task: " + e.message);
    }
    setBusy(false);
  };

  const toggle = async (id) => {
    const t = tasks.find(x => x.id === id);
    if (!t || !t.hsId) return;
    const newDone = !t.done;
    // Optimistic update
    setTasks(prev => prev.map(x => x.id === id ? {...x, done: newDone} : x));
    try {
      await HS.updateTaskInHubSpot(t.hsId, {done: newDone});
    } catch (e) {
      // Revert on failure
      setTasks(prev => prev.map(x => x.id === id ? {...x, done: !newDone} : x));
      setErrMsg("Could not update task: " + e.message);
      setTimeout(() => setErrMsg(""), 4000);
    }
  };

  const del = async (id) => {
    const t = tasks.find(x => x.id === id);
    if (!t || !t.hsId) return;
    // Optimistic remove
    const prevTasks = tasks;
    setTasks(prev => prev.filter(x => x.id !== id));
    try {
      await HS.deleteTaskInHubSpot(t.hsId);
    } catch (e) {
      // Revert on failure
      setTasks(prevTasks);
      setErrMsg("Could not delete task: " + e.message);
      setTimeout(() => setErrMsg(""), 4000);
    }
  };

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const today = new Date().toISOString().split("T")[0];
  const isOver = t => !t.done && t.dueDate && t.dueDate < today;
  const isTod  = t => !t.done && t.dueDate === today;

  const filtered = tasks.filter(t => {
    if (filter === "overdue")  return isOver(t);
    if (filter === "today")    return isTod(t);
    if (filter === "upcoming") return !t.done && t.dueDate > today;
    if (filter === "done")     return t.done;
    return true;
  }).sort((a, b) => {
    if (isOver(a) && !isOver(b)) return -1;
    if (!isOver(a) && isOver(b)) return  1;
    return (a.dueDate || "z").localeCompare(b.dueDate || "z");
  });

  const ovCt = tasks.filter(t => isOver(t)).length;
  const tdCt = tasks.filter(t => isTod(t)).length;

  const showsMultipleUsers = isAdmin && viewUser === "all" && viewedUsers.length > 1;
  const userLabel = (uid) => {
    const u = findOwner(uid, users || []);
    if (!u) return null;
    return u.name || (u.email ? u.email.split("@")[0] : "User");
  };

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      {/* Admin user picker */}
      {isAdmin && (users || []).filter(u => !u.isAdmin).length > 0 && (
        <div style={{background:"#fff",borderRadius:14,padding:"10px 14px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",display:"inline-flex",alignItems:"center",gap:10,marginBottom:14}}>
          <span style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Viewing</span>
          <select value={viewUser} onChange={e=>setViewUser(e.target.value)}
            style={{padding:"6px 12px",borderRadius:8,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,fontFamily:"Barlow,sans-serif",fontWeight:600,color:"var(--brown)",cursor:"pointer",outline:"none"}}>
            <option value="all">👥 All Users ({(users||[]).filter(u=>!u.isAdmin).length})</option>
            <option value="me">👤 My Tasks (Admin)</option>
            <optgroup label="Sales Reps">
              {(users || []).filter(u => !u.isAdmin).map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </optgroup>
          </select>
        </div>
      )}

      {/* Filter chips */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        {[
          {id:"all",      label:"All ("+tasks.length+")"},
          {id:"overdue",  label:"Overdue ("+ovCt+")", danger: ovCt > 0},
          {id:"today",    label:"Due Today ("+tdCt+")"},
          {id:"upcoming", label:"Upcoming"},
          {id:"done",     label:"Completed"},
        ].map(f => (
          <button key={f.id} onClick={()=>setFilter(f.id)}
            style={{
              padding:"7px 14px",borderRadius:99,fontSize:12,fontWeight:600,
              cursor:"pointer",fontFamily:"Barlow,sans-serif",transition:"all .15s",
              border:"1.5px solid " + (filter === f.id ? (f.danger ? "var(--red)" : "var(--brown)") : "var(--warm2)"),
              background:filter === f.id ? (f.danger ? "#fdecea" : "var(--warm1)") : "#fff",
              color:filter === f.id ? (f.danger ? "var(--red)" : "var(--brown)") : "var(--muted)",
            }}>
            {f.label}
          </button>
        ))}
        {loading && <span style={{fontSize:11,fontStyle:"italic",color:"var(--muted)",display:"inline-flex",alignItems:"center",gap:6}}><Spin/> syncing from HubSpot...</span>}
        <div style={{marginLeft:"auto"}}>
          <Btn variant="primary" onClick={()=>setShowAdd(v => !v)} style={{fontSize:12,padding:"7px 16px"}} disabled={busy}>+ New Task</Btn>
        </div>
      </div>

      {/* Color legend for multi-user merged view */}
      {showsMultipleUsers && (
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:14,padding:"8px 14px",background:"#fff",borderRadius:10,border:"1px solid var(--warm2)"}}>
          <span style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Users:</span>
          {viewedUsers.map(u => (
            <span key={u.id} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12}}>
              <span style={{width:12,height:12,borderRadius:99,background:colorForUser(u.id),flexShrink:0}}/>
              <span style={{color:"var(--text)",fontWeight:500}}>{u.name || u.email.split("@")[0]}</span>
            </span>
          ))}
        </div>
      )}

      {/* Error banner */}
      {errMsg && (
        <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13,marginBottom:14}}>
          {errMsg}
        </div>
      )}

      {/* New task form */}
      {showAdd && (
        <div style={{background:"#fff",borderRadius:14,padding:"18px 20px",border:"1.5px dashed var(--warm3)",marginBottom:16,display:"flex",flexDirection:"column",gap:10}}>
          <div style={{fontSize:14,fontWeight:600,color:"var(--brown)"}}>New Task</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <FI label="Task Title" value={nw.title} onChange={v => setNw(t => ({...t, title: v}))} placeholder="e.g. Call Kingsdown"/>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Type</label>
              <select value={nw.type} onChange={e=>setNw(t=>({...t, type: e.target.value}))}
                style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none"}}>
                <option value="call">📞 Call</option>
                <option value="visit">🤝 Visit</option>
                <option value="email">✉ Email</option>
                <option value="quote">📋 Quote</option>
                <option value="other">📌 Other</option>
              </select>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Due Date</label>
              <input type="date" value={nw.dueDate} onChange={e=>setNw(t=>({...t, dueDate: e.target.value}))}
                style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif"}}/>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Company</label>
              <select value={nw.companyId} onChange={e=>setNw(t=>({...t, companyId: e.target.value}))}
                style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:13,outline:"none"}}>
                <option value="">- None -</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.properties.name}</option>)}
              </select>
            </div>
          </div>
          <FI label="Notes" value={nw.note} onChange={v=>setNw(t=>({...t, note: v}))} placeholder="Optional..." multiline rows={2}/>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={add} disabled={!nw.title || busy}>{busy ? <><Spin/> Saving...</> : "Save Task"}</Btn>
            <Btn variant="ghost" onClick={()=>{setShowAdd(false); setNw({title:"",type:"call",dueDate:"",companyId:"",note:""});}}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Task list */}
      {filtered.length === 0 && !loading && (
        <div style={{padding:60,textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>
          {tasks.length === 0 ? "No tasks yet - add one above!" : "No tasks match this filter."}
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {filtered.map(t => {
          const co = companies.find(c => c.id === t.companyId);
          const over = isOver(t);
          const tod = isTod(t);
          const tUserName = t.ownerId ? userLabel(t.ownerId) : null;
          const tUserColor = t.ownerId ? colorForUser(t.ownerId) : null;
          return (
            <div key={t.id} style={{
              background:"#fff",borderRadius:12,padding:"12px 14px",
              border:"1px solid " + (over ? "var(--red)" : "var(--warm2)"),
              boxShadow:"var(--sh)",
              display:"flex",alignItems:"flex-start",gap:12,
              opacity:t.done?.55:1,
              borderLeft: showsMultipleUsers && tUserColor ? "4px solid " + tUserColor : undefined,
            }}>
              <input type="checkbox" checked={t.done} onChange={()=>toggle(t.id)}
                style={{width:18,height:18,accentColor:"var(--brown)",cursor:"pointer",flexShrink:0,marginTop:2}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{fontWeight:600,fontSize:14,textDecoration:t.done?"line-through":"none"}}>
                    <span style={{marginRight:6}}>{TI[t.type] || "📌"}</span>
                    {t.title}
                  </div>
                  <button onClick={()=>del(t.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--muted)",fontSize:14,padding:"0 4px",opacity:.5}}
                    onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                    onMouseLeave={e=>e.currentTarget.style.opacity=".5"}>✕</button>
                </div>
                <div style={{display:"flex",gap:10,fontSize:11,color:"var(--muted)",marginTop:4,flexWrap:"wrap"}}>
                  {t.dueDate && (
                    <span style={{color:over?"var(--red)":tod?"var(--brown)":"var(--muted)",fontWeight:over||tod?600:400}}>
                      📅 {new Date(t.dueDate + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                      {over && " (overdue)"}
                      {tod && " (today)"}
                    </span>
                  )}
                  {co && <span>🏢 {co.properties.name}</span>}
                  {showsMultipleUsers && tUserName && <span style={{color:tUserColor,fontWeight:600}}>👤 {tUserName}</span>}
                </div>
                {t.note && <div style={{fontSize:12,color:"var(--muted)",marginTop:6,fontStyle:"italic"}}>{t.note}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
